import { useQuery } from "@tanstack/react-query";
import CategoriesSection from "../components/home/categories-section";
import Hero from "../components/home/hero";
import ProductsSection from "../components/home/products-section";
import { getHomePage } from "../actions/getHomePage";
import { useTranslation } from "react-i18next";
import HomeProductsSection from "../components/home/home-products-section";
import HomeShopsSection from "../components/home/home-shops-section";
import LoadingScreen from "../components/loading-screen";
import useUserStore from "../store/userStore";
import { Helmet } from "react-helmet-async";
import SpecialOffersSection from "../components/home/specialoffers-section";
import BlockbusterDeals from "../components/home/blockbuster_deals";
import { getCountry } from "../lib/middleware";
import BannersModal from "../components/modals/banners-modal";
import { useState, useEffect, useMemo } from "react";
import useModalsStore from "../store/modalsStore";

export function normalizeNationalDay(data: any, country: string | null) {
    if (!data) return data;
    const normalized = {
        ...data,
        ksa_national_day: false,
        oman_national_day: false,
        uae_national_day: false,
    };
    if (country === "ksa" && data.ksa_national_day) {
        normalized.ksa_national_day = true;
        return normalized;
    }
    if (country === "om" && data.oman_national_day) {
        normalized.oman_national_day = true;
        return normalized;
    }
    if ((!country || country === "uae") && data.uae_national_day) {
        normalized.uae_national_day = true;
        return normalized;
    }
    return normalized;
}

export default function Home() {
    const { t } = useTranslation();
    const mainAddress = useUserStore((state) => state.mainAddress);
    const setMainAddress = useUserStore((state) => state.setMainAddress);
    const { data, isFetching } = useQuery({
        queryKey: ["global", "home-page"],
        queryFn: getHomePage,
    });
    // console.log(getCountry());
    // console.log(data);
    const country = getCountry() ?? null;

    // open only on first visit (tracked in localStorage)
    const [isBannersModalOpen, setIsBannersModalOpen] = useState(false);
    const {
        isLanguageCountryModalOpen,
        isLoginModalOpen,
        isOtpModalOpen,
        isRegisterModalOpen,
        isSelectPreviousAddressModalOpen,
        isDeleteCartModalOpen,
        isModalOpen,
        isMapModalOpen,
        isAddingNewAddress,
    } = useModalsStore((state) => ({
        isLanguageCountryModalOpen: state.isLanguageCountryModalOpen,
        isLoginModalOpen: state.isLoginModalOpen,
        isOtpModalOpen: state.isOtpModalOpen,
        isRegisterModalOpen: state.isRegisterModalOpen,
        isSelectPreviousAddressModalOpen:
            state.isSelectPreviousAddressModalOpen,
        isDeleteCartModalOpen: state.isDeleteCartModalOpen,
        isModalOpen: state.isModalOpen,
        isMapModalOpen: state.isMapModalOpen,
        isAddingNewAddress: state.isAddingNewAddress,
    }));

    const anyModalOpen =
        isLanguageCountryModalOpen ||
        isLoginModalOpen ||
        isOtpModalOpen ||
        isRegisterModalOpen ||
        isSelectPreviousAddressModalOpen ||
        isAddingNewAddress ||
        isDeleteCartModalOpen ||
        isModalOpen ||
        isMapModalOpen;

    useEffect(() => {
        if (!data) return;
        const hasShown = (() => {
            try {
                return (
                    localStorage.getItem("zabehaty_home_banners_shown") === "1"
                );
            } catch (e) {
                return false;
            }
        })();
        // Only show banners if not shown before, banners exist, and no modal is open
        if (
            !hasShown &&
            data.banners &&
            data.banners.length > 0 &&
            !anyModalOpen
        ) {
            setIsBannersModalOpen(true);
        }
    }, [data, anyModalOpen]);

    // const normalizedData = useMemo(() => {
    //     return normalizeNationalDay(data, country);
    // }, [data, country]);
    // if (!normalizedData) return null;

    if (data) {
        if (data.ksa_national_day === true && getCountry() === "ksa") {
            data.ksa_national_day = true;
        } else {
            data.ksa_national_day = false;
        }
        if (data?.oman_national_day === true && getCountry() === "om") {
            data.oman_national_day = true;
        } else {
            data.oman_national_day = false;
        }
        // const sectionsWithBackground = data.home_sections.map(
        //     (section, index) => {
        //         if (index === 0) {
        //             return {
        //                 ...section,
        //                 background_image: {
        //                     mobile: "",
        //                     web: "/UAE.png",
        //                 },
        //             };
        //         }
        //         return section;
        //     },
        // );
        // data.home_sections = sectionsWithBackground;
        // if (data?.uae_national_day === true && getCountry() === "uae") {
        //     data.uae_national_day = true;
        // } else {
        //     data.uae_national_day = false;
        // }
    }
    if (isFetching) return <LoadingScreen />;
    if (!data) return null;

    // Facebook Open Graph Meta Tags
    const metaTitle = "Zabehaty - Online Qurbani & Fresh Meat Delivery";
    const metaDescription =
        "An Emirati company working to provide and develop services for supplying carcasses and livestock in an advanced manner to keep pace with modern life.";
    const featuredProduct = data?.home_sections?.[0]?.items?.[0]?.item;
    const ogImage =
        featuredProduct?.image || "https://zabehaty.com/default-og-image.jpg";
    const ogTitle = featuredProduct?.name || metaTitle;
    const ogDescription = featuredProduct?.description || metaDescription;

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta
                    name="description"
                    content="const metaDescription = `Zabehaty – الأكثر مبيعاً من اللحوم والخضار والفواكه. Online fresh meat & livestock delivery UAE.`;
"
                />
                <meta
                    name="keywords"
                    content="zabehaty, qurbani, meat, organic, butcher, halal, UAE, KSA, delivery, online shopping, fresh meat, livestock, online meat delivery, fresh produce, limited-time offers"
                />
                <meta
                    name="keywords"
                    content="الأكثر, مبيعاً, ذبيحتي, الملحمة, خضار, ملحمة, المنتجة, فواكه, الأكثر مبيعاً, دإ الأكثر, دإ الأكثر مبيعاً, الأسر المنتجة, خضار و, و فواكه, خضار و فواكه, نبع الاصاله"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content="https://zabehaty.com" />
                <meta property="og:site_name" content="Zabehaty" />

                {/* Snapchat */}
                <meta
                    property="snapchat:sticker"
                    content="https://kit.snapchat.com/ckweb/test/image.png"
                />
            </Helmet>

            <BannersModal
                isOpen={isBannersModalOpen}
                setIsOpen={setIsBannersModalOpen}
                banners={data.banners}
            />

            <div
                className={` relative flex flex-col gap-y-10 py-10 lg:gap-y-0 xl:gap-y-0 ${
                    data.oman_national_day
                        ? "bg-[#FFFFFF]"
                        : data.ksa_national_day
                          ? "bg-[#e6f0e7]"
                          : "bg-none"
                }`}
                style={
                    data.background_image
                        ? {
                              background: `url(${data?.background_image?.web}) no-repeat center center / cover`,
                          }
                        : data.uae_national_day
                          ? {
                                background:
                                    "linear-gradient(179.76deg, rgba(239, 239, 239, 0.1) 9.97%, rgba(250, 46, 53, 0.1) 30.46%, rgba(1, 176, 68, 0.1) 37.85%, rgba(183, 183, 183, 0.1) 54.94%)",
                            }
                          : {}
                }
            >
                {/* Remove bg-[#e6f0e7] */}
                <h1 className="sr-only">
                    Zabehaty – الأكثر مبيعاً من اللحوم والخضار والفواكه
                </h1>
                <h2 className="sr-only">الأكثر , مبيعاً , ذبيحتي , الملحمة</h2>
                <h4 className="sr-only">خضار , ملحمة , المنتجة , فواكه</h4>
                <h5 className="sr-only">
                    الأكثر مبيعاً , دإ الأكثر , دإ الأكثر مبيعاً , الأسر المنتجة
                </h5>
                <h6 className="sr-only">
                    خضار و , و فواكه , خضار و فواكه , نبع الاصاله
                </h6>

                <Hero sliders={data.slider} />
                {data?.uae_national_day == true && (
                    <>
                        <img
                            src="/uaenationalday/fireworks1.svg"
                            alt="Decorative Fireworks"
                            className="absolute left-[0px] top-[450px] z-10 h-24 w-24"
                        />

                        {/* <img
                            src="/uaenationalday/fireworks2.svg"
                            alt="Decorative Fireworks"
                            className="absolute right-[0px] top-[900px] z-10 h-28 w-28 opacity-90"
                        /> */}

                        <img
                            src="/uaenationalday/fireworks3.svg"
                            alt="Decorative Fireworks"
                            className="absolute right-[-0px] top-[1700px] z-10 h-20 w-20 opacity-70"
                        />

                        <img
                            src="/uaenationalday/fireworks4.svg"
                            alt="Decorative Fireworks"
                            className="absolute left-[-0px] top-[2200px] z-10 h-24 w-24"
                        />

                        <img
                            src="/uaenationalday/fireworks5.svg"
                            alt="Decorative Fireworks"
                            className="absolute right-[-0px] top-[2900px] z-10 h-28 w-28 opacity-80"
                        />

                        <img
                            src="/uaenationalday/fireworks6.svg"
                            alt="Decorative Fireworks"
                            className="absolute left-[-0px] top-[3400px] z-10 h-20 w-20 opacity-60"
                        />

                        <img
                            src="/uaenationalday/fireworks7.svg"
                            alt="Decorative Fireworks"
                            className="absolute bottom-10 bottom-[1000px] right-[-0px] z-10 h-24 w-24"
                        />
                    </>
                )}
                <CategoriesSection
                    categories={data.categories}
                    ksa_national_day={data.ksa_national_day}
                    oman_national_day={data.oman_national_day}
                    uae_national_day={data.uae_national_day}
                />

                {/* KSA Special Offers Section */}
                {data.home_sections.map((section) => {
                    if (
                        section.type === "product" &&
                        section.items.length > 0 &&
                        section.ksa_national_day === true
                    ) {
                        return (
                            <HomeProductsSection
                                key={section.name}
                                items={section.items}
                                title={section.name}
                                ksa_national_day={section.ksa_national_day}
                                oman_national_day={null}
                                uae_national_day={null}
                            />
                        );
                    }
                })}

                {/* OMAN Special Offers Section */}
                {data.home_sections.map((section) => {
                    if (
                        section.type === "product" &&
                        section.items.length > 0 &&
                        section.oman_national_day === true
                    ) {
                        return (
                            <HomeProductsSection
                                key={section.name}
                                items={section.items}
                                title={section.name}
                                oman_national_day={section.oman_national_day}
                                ksa_national_day={null}
                                uae_national_day={null}
                            />
                        );
                    }
                })}

                {/* UAE Special Offers Section */}
                {data.home_sections.map((section) => {
                    if (
                        section.type === "product" &&
                        section.items.length > 0 &&
                        section.uae_national_day === true
                    ) {
                        return (
                            <HomeProductsSection
                                key={section.name}
                                items={section.items}
                                title={section.name}
                                uae_national_day={section.uae_national_day}
                                oman_national_day={null}
                                ksa_national_day={null}
                            />
                        );
                    }
                })}

                {/* KSA BlockBuster Section */}
                {/* {data.ksa_national_day === true && <BlockbusterDeals />} */}

                <div className="mx-10 mb-10 flex flex-col gap-y-10 xl:mx-24 xl:mb-20 xl:gap-y-[68px]">
                    {data.limited_offer && data.limited_offer.length > 0 && (
                        <ProductsSection
                            title={t("limited-offers")}
                            products={data.limited_offer}
                        />
                    )}

                    {data.home_sections.map((section) => {
                        if (
                            section.type === "product" &&
                            section.items.length > 0 &&
                            section.is_market === 0 &&
                            section.ksa_national_day !== true &&
                            section.uae_national_day !== true &&
                            section.oman_national_day !== true
                        ) {
                            return (
                                <HomeProductsSection
                                    key={section.name}
                                    items={section.items}
                                    title={section.name}
                                    ksa_national_day={null}
                                    oman_national_day={null}
                                    uae_national_day={null}
                                    background_image={
                                        section.background_image?.web
                                    }
                                />
                            );
                        }
                        if (
                            section.type === "shop" &&
                            section.items.length > 0 &&
                            section.is_market === 0
                        ) {
                            return (
                                <HomeShopsSection
                                    key={section.name}
                                    items={section.items}
                                    title={section.name}
                                />
                            );
                        }
                        return null;
                    })}
                </div>
            </div>
        </>
    );
}
